import { forwardRef, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button, type ButtonProps } from "@/components/ui/button";

/**
 * Button whose destination is admin-editable.
 * href rules: "" -> run onFallback (e.g. open a popup form),
 * "#id" -> smooth-scroll to that section, "http(s)/mailto/tel" -> external,
 * anything else -> internal page link.
 */
interface CmsButtonProps extends Omit<ButtonProps, "asChild" | "onClick"> {
  href?: string;
  onFallback?: () => void;
  children: ReactNode;
}

const CmsButton = forwardRef<HTMLButtonElement, CmsButtonProps>(
  ({ href, onFallback, children, ...props }, ref) => {
    const link = (href || "").trim();

    if (!link) {
      return <Button ref={ref} {...props} onClick={onFallback}>{children}</Button>;
    }
    if (link.startsWith("#")) {
      return (
        <Button
          ref={ref}
          {...props}
          onClick={() => document.getElementById(link.slice(1))?.scrollIntoView({ behavior: "smooth" })}
        >
          {children}
        </Button>
      );
    }
    if (/^(https?:|mailto:|tel:)/i.test(link)) {
      const external = /^https?:/i.test(link);
      return (
        <Button {...props} asChild>
          <a href={link} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>{children}</a>
        </Button>
      );
    }
    return (
      <Button {...props} asChild>
        <Link to={link.startsWith("/") ? link : `/${link}`}>{children}</Link>
      </Button>
    );
  },
);
CmsButton.displayName = "CmsButton";

export default CmsButton;
